'use client'

import {createContext, useState, useContext, ReactNode, FC} from 'react'
import {updateFavGames} from '@app/components/game-player/api/update-fav-games'
import {addRecentlyPlayedGame} from '@app/components/game-player/api/add-recently-played-game'

interface Preferences {
  favourite: string[]
  lastPlayed: string[]
}

interface PreferencesContextProps {
  preferences: Preferences
  updateFavourite: (gameCode: string, propertyCode: string, subId: string) => void
  updateLastPlayed: (gameCode: string, propertyCode: string, subId: string) => void
}

const PreferencesContext = createContext<PreferencesContextProps>({} as PreferencesContextProps)

interface PreferencesProviderProps {
  children: ReactNode
  initialPreferences: Preferences
}

export const PreferencesProvider: FC<PreferencesProviderProps> = ({children, initialPreferences}) => {
  const [preferences, setPreferences] = useState<Preferences>(initialPreferences)

  const updateFavourite = async (gameCode: string, propertyCode: string, subId: string) => {
    // Temporarily store the current favorite status
    const {favourite} = preferences
    const isCurrentlyFavourite = favourite.includes(gameCode)

    // Optimistically update the preferences
    setPreferences((prevPreferences) => {
      const updatedFavourites = isCurrentlyFavourite
        ? prevPreferences.favourite.filter((item) => item !== gameCode)
        : [...prevPreferences.favourite, gameCode]

      return {
        ...prevPreferences,
        favourite: updatedFavourites,
      }
    })

    // Make the API call
    const {error} = await updateFavGames({propertyCode, gameCode, subId, isFav: !isCurrentlyFavourite})

    // Rollback if there's an error
    if (error) {
      setPreferences((prevPreferences) => {
        const restoredFavourites = isCurrentlyFavourite
          ? [...prevPreferences.favourite, gameCode]
          : prevPreferences.favourite.filter((item) => item !== gameCode)

        return {
          ...prevPreferences,
          favourite: restoredFavourites,
        }
      })
    }
  }

  const updateLastPlayed = async (gameCode: string, propertyCode: string, subId: string) => {
    let rollback = false

    setPreferences((prevPreferences) => {
      const filteredLastPlayed = prevPreferences.lastPlayed.filter((item) => item !== gameCode)
      const newLastPlayed = [gameCode, ...filteredLastPlayed]
      rollback = !prevPreferences.lastPlayed.includes(gameCode) // Determine if rollback is needed

      return {
        ...prevPreferences,
        lastPlayed: newLastPlayed,
      }
    })

    const {error} = await addRecentlyPlayedGame({propertyCode, gameCode: gameCode, subId})

    if (error) {
      setPreferences((prevPreferences) => {
        const newLastPlayed = rollback
          ? prevPreferences.lastPlayed.filter((item) => item !== gameCode) // Rollback if added
          : [gameCode, ...prevPreferences.lastPlayed] // Rollback if not added

        return {
          ...prevPreferences,
          lastPlayed: newLastPlayed,
        }
      })
    }
  }

  return (
    <PreferencesContext.Provider value={{preferences, updateFavourite, updateLastPlayed}}>
      {children}
    </PreferencesContext.Provider>
  )
}

export const useGamePreference = (): PreferencesContextProps => {
  const context = useContext(PreferencesContext)
  if (!context) {
    throw new Error('useGamePreference must be used within a PreferencesProvider')
  }
  return context
}
